import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import GlobalStyles from "@mui/material/GlobalStyles";
import CssBaseline from "@mui/material/CssBaseline";

import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import { getStaffFrontendUrl, isStaffRole, navigateAccountPortal } from "lib/staffFrontend";
import { mobileBottomNavItems } from "mobile/components/mobileNavConfig";
import MobileSearchOverlay from "mobile/components/MobileSearchOverlay";
import { useMobileLocale } from "mobile/i18n";

const NAV = {
  ink: "#0F0F12",
  muted: "#6E7278",
  subtle: "#8A8F96",
  accent: "#FF5C00",
  hairline: "rgba(15, 15, 18, 0.1)",
  surface: "rgba(255,255,255,0.94)",
  canvas: "#F7F8FA",
  danger: "#A51812",
  swoosh: "linear-gradient(90deg, #FFB000 0%, #FF5C00 35%, #FF1A1A 70%, #D70000 100%)",
};

const MOBILE_BOTTOM_NAV_HEIGHT = 62;
const MOBILE_BOTTOM_NAV_PAD = 6;

const isActivePath = (pathname, target) => {
  if (!target) return false;
  if (target === "/m") return pathname === "/m" || pathname === "/m/";
  return pathname === target || pathname.startsWith(`${target}/`);
};

const mobileGlobalStyles = {
  "html, body": { overscrollBehaviorY: "none" },
  body: {
    fontSize: "14px",
    lineHeight: 1.35,
    WebkitTextSizeAdjust: "100%",
  },
  ".MuiAppBar-root": { display: "none !important" },
  ".MuiTypography-h3": { fontSize: "1.35rem !important", lineHeight: "1.25 !important" },
  ".MuiTypography-h4": { fontSize: "1.2rem !important", lineHeight: "1.25 !important" },
  ".MuiTypography-h5": { fontSize: "1.1rem !important", lineHeight: "1.25 !important" },
  ".MuiTypography-h6": { fontSize: "1.0rem !important", lineHeight: "1.25 !important" },
  ".MuiTypography-subtitle1": { fontSize: "0.95rem !important" },
  ".MuiTypography-subtitle2": { fontSize: "0.9rem !important" },
  ".MuiTypography-body1": { fontSize: "0.95rem !important" },
  ".MuiTypography-body2": { fontSize: "0.9rem !important" },
  ".MuiTypography-caption": { fontSize: "0.75rem !important" },
  ".MuiButton-root": {
    textTransform: "none",
    borderRadius: "12px",
    minHeight: "36px",
    paddingTop: "7px",
    paddingBottom: "7px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.85rem",
    fontWeight: 700,
  },
  ".MuiIconButton-root": { padding: "8px" },
  ".MuiInputBase-root": { fontSize: "0.9rem" },
  ".MuiInputBase-input": { fontSize: "16px !important" },
  ".MuiOutlinedInput-input": { paddingTop: "10px", paddingBottom: "10px", fontSize: "16px !important" },
  ".MuiChip-root": { height: "26px" },
  ".MuiChip-label": { fontSize: "0.75rem", fontWeight: 700 },
  ".MuiTableCell-root": { padding: "6px 8px" },
  ".MuiCard-root": { borderRadius: "16px" },
  ".MuiCardContent-root": { padding: "12px" },
  ".MuiButtonBase-root": { WebkitTapHighlightColor: "transparent" },
  ".MuiTypography-root": {
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
};

const getContextKey = (pathname) => {
  const p = pathname.startsWith("/m") ? pathname.slice(2) || "/" : pathname;
  if (p === "/" || p === "") return "public";
  if (p.startsWith("/catalog") || p.startsWith("/valve-bodies")) return "catalog";
  if (p.startsWith("/cart") || p.startsWith("/checkout")) return "cart";
  if (p.startsWith("/account")) return "account";
  if (p.startsWith("/authentication") || p.startsWith("/register") || p.startsWith("/scan")) return "public";
  return "public";
};

const CONTEXT_NAV = {
  public: ["home", "catalog", "cart"],
  catalog: ["home", "catalog", "cart"],
  cart: ["home", "catalog", "cart", "account"],
  account: ["account", "catalog", "cart", "home"],
};

function buildMenuSections({ token, pluginRole, t }) {
  const shop = {
    key: "shop",
    title: t("Shop"),
    items: [
      { key: "home", name: t("Home"), to: "/m" },
      { key: "catalog", name: t("Catalog"), to: "/m/catalog" },
      { key: "cart", name: t("Cart"), to: "/m/cart" },
    ],
  };

  if (!token) {
    return [
      shop,
      {
        key: "guest",
        title: t("Account"),
        items: [
          { key: "sign-in", name: t("Sign in"), to: "/m/authentication/sign-in" },
          { key: "register", name: t("Create an account"), to: "/m/register" },
        ],
      },
    ];
  }

  if (isStaffRole(pluginRole)) {
    return [
      shop,
      {
        key: "staff",
        title: t("Staff"),
        items: [{ key: "staff-erp", name: t("Open Staff ERP"), href: getStaffFrontendUrl("/") }],
      },
    ];
  }

  const distributor = isDistributorRole(pluginRole);
  return [
    shop,
    {
      key: "account",
      title: t("Account"),
      items: [
        { key: "account-home", name: t("Account"), to: "/m/account" },
        ...(distributor
          ? [
              { key: "distributor-requests", name: t("Order requests"), to: "/m/account/distributor-requests" },
              { key: "distributor-customers", name: t("Linked customers"), to: "/m/account/distributor-customers" },
            ]
          : []),
        {
          key: "valve-bodies",
          name: distributor ? t("Sold valve bodies") : t("Valve bodies"),
          to: "/m/account/valve-bodies",
        },
        { key: "orders", name: t("Orders"), to: "/m/account/orders" },
        { key: "support", name: t("Support"), to: "/m/account/support" },
        { key: "profile", name: t("Profile"), to: "/m/account/profile" },
        { key: "addresses", name: t("Addresses"), to: "/m/account/addresses" },
      ],
    },
  ];
}

function NavTab({ item, active, badge, label, onClick }) {
  const IconComp = item.icon;

  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: 96,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.35,
        py: 0.5,
        px: 0.25,
        borderRadius: "14px",
        position: "relative",
        color: active ? NAV.accent : NAV.muted,
        transition: "color 160ms ease, transform 120ms ease",
        "&:active": { transform: "scale(0.96)" },
      }}
    >
      {active ? (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: 22,
            height: 3,
            borderRadius: 99,
            background: NAV.swoosh,
          }}
        />
      ) : null}

      <Box
        sx={{
          width: active ? 44 : 36,
          height: active ? 44 : 36,
          borderRadius: active ? "14px" : "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: active ? NAV.accent : "transparent",
          color: active ? "#fff" : NAV.muted,
          boxShadow: active ? "0 8px 18px rgba(255,92,0,0.32)" : "none",
          transition: "background-color 160ms ease, box-shadow 160ms ease, color 160ms ease, width 160ms ease, height 160ms ease",
          mt: active ? -1.25 : 0,
        }}
      >
        <Badge
          badgeContent={badge || 0}
          color="error"
          invisible={!badge}
          sx={{
            "& .MuiBadge-badge": {
              fontWeight: 800,
              fontSize: 10,
              minWidth: 16,
              height: 16,
              top: 2,
              right: 2,
            },
          }}
        >
          <IconComp fontSize={active ? 24 : 22} />
        </Badge>
      </Box>

      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: active ? 800 : 650,
          letterSpacing: 0.15,
          lineHeight: 1.1,
          color: active ? NAV.accent : NAV.muted,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}

NavTab.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  active: PropTypes.bool,
  badge: PropTypes.number,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

NavTab.defaultProps = {
  active: false,
  badge: 0,
};

function MenuRow({ item, selected, divider, danger, onClick }) {
  return (
    <>
      <ButtonBase
        onClick={onClick}
        sx={{
          width: "100%",
          minHeight: 48,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.25,
          textAlign: "left",
          bgcolor: selected ? "rgba(255,92,0,0.07)" : "transparent",
          "&:active": { bgcolor: "rgba(15,15,18,0.05)" },
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: selected ? 700 : 600,
            lineHeight: 1.25,
            color: danger ? NAV.danger : selected ? NAV.accent : NAV.ink,
            letterSpacing: -0.2,
          }}
        >
          {item.name}
        </Typography>
        {danger ? null : (
          <Icon sx={{ fontSize: 20, color: selected ? NAV.accent : "rgba(15,15,18,0.28)", flexShrink: 0 }}>
            chevron_right
          </Icon>
        )}
      </ButtonBase>
      {divider ? <Box sx={{ height: "1px", bgcolor: "rgba(15,15,18,0.08)", ml: 2 }} /> : null}
    </>
  );
}

MenuRow.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  selected: PropTypes.bool,
  divider: PropTypes.bool,
  danger: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

MenuRow.defaultProps = {
  selected: false,
  divider: true,
  danger: false,
};

export default function MobileShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, pluginRole } = useAuth();
  const { itemCount } = useCart();
  const { t } = useMobileLocale();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const contextKey = getContextKey(location.pathname);
  const pathname = location.pathname || "";
  // Immersive chat surfaces: hide dock so compose can sit flush to the bottom.
  const hideBottomNav =
    pathname === "/m/account/support/new" ||
    (pathname.startsWith("/m/account/support/") && pathname !== "/m/account/support/") ||
    /\/m\/account\/warranty-claims\/[^/]+\/chat\/?$/.test(pathname);
  // Pages with their own sticky/absolute top chrome already apply safe-area.
  // Extra MobileShell top padding creates a blank "second header" above them.
  const ownsTopChrome =
    pathname === "/m" ||
    pathname === "/m/" ||
    pathname === "/m/download" ||
    pathname.startsWith("/m/catalog") ||
    pathname.startsWith("/m/cart") ||
    pathname.startsWith("/m/checkout") ||
    pathname.startsWith("/m/scan") ||
    pathname.startsWith("/m/account") ||
    pathname.startsWith("/m/authentication") ||
    pathname.startsWith("/m/register") ||
    pathname.startsWith("/m/qr") ||
    pathname.startsWith("/m/deal") ||
    // Staff mobile pages use MobileTopBar with its own safe-area inset.
    Boolean(
      [
        "/m/dashboard",
        "/m/pipeline",
        "/m/work-orders",
        "/m/inventory",
        "/m/sales",
        "/m/pull-orders",
        "/m/invoices",
        "/m/payments",
        "/m/customers",
        "/m/vendors",
        "/m/purchase",
        "/m/warehouse",
        "/m/valve-bodies",
        "/m/internal/",
        "/m/media/",
        "/m/projections",
        "/m/settings",
        "/m/search",
      ].some((prefix) => pathname === prefix || pathname.startsWith(prefix)),
    );

  const bottomNavItems = useMemo(() => {
    const available = mobileBottomNavItems
      .filter((i) => i.public || !!token)
      .filter((i) => !(i.hideWhenAuthed && !!token))
      .filter((i) => !["dashboard", "pipeline", "work-orders", "inventory", "search", "login"].includes(i.key));

    const order = CONTEXT_NAV[contextKey] || CONTEXT_NAV.public;
    const menuItem = available.find((i) => i.key === "menu") || null;
    const picked = order
      .map((k) => available.find((i) => i.key === k))
      .filter(Boolean)
      .slice(0, 3);

    return menuItem ? [...picked, menuItem] : picked;
  }, [token, contextKey]);

  const bottomNavValue = useMemo(() => {
    if (menuOpen) return "menu";
    const pathname = location.pathname || "";
    const onCheckout = pathname === "/m/checkout" || pathname.startsWith("/m/checkout/");
    const match = bottomNavItems.find((i) => {
      if (i.key === "menu") return false;
      // Checkout is cart-context; do not fall through to Home (elevated orange Home stole Pay taps).
      if (i.key === "cart" && (onCheckout || isActivePath(pathname, i.to))) return true;
      if (i.key === "cart") return false;
      return isActivePath(pathname, i.to);
    });
    return match?.key || "home";
  }, [bottomNavItems, location.pathname, menuOpen]);

  const menuSections = useMemo(
    () => buildMenuSections({ token, pluginRole, t }),
    [token, pluginRole, t]
  );

  const handleNav = (item) => {
    if (!item) return;
    if (item.key === "search") {
      setSearchOpen(true);
      return;
    }
    if (item.key === "menu") {
      setMenuOpen((open) => !open);
      return;
    }
    if (item.key === "account" && isStaffRole(pluginRole)) {
      navigateAccountPortal({ token, role: pluginRole, navigate });
      return;
    }
    if (item.to) navigate(item.to);
  };

  const handleMenuItem = (item) => {
    setMenuOpen(false);
    if (item.href) {
      window.location.assign(item.href);
      return;
    }
    if (item.to) navigate(item.to);
  };

  const showSignOut = !!token && (isPortalRole(pluginRole) || isStaffRole(pluginRole) || !pluginRole);

  return (
    <>
      <CssBaseline />
      <GlobalStyles styles={mobileGlobalStyles} />

      <MobileSearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onNavigate={(to) => {
          setSearchOpen(false);
          if (to) navigate(to);
        }}
      />

      <SwipeableDrawer
        anchor="bottom"
        open={menuOpen}
        onOpen={() => setMenuOpen(true)}
        onClose={() => setMenuOpen(false)}
        disableDiscovery
        swipeAreaWidth={0}
        ModalProps={{
          keepMounted: false,
          sx: {
            zIndex: 1600,
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(15,15,18,0.36)",
            },
          },
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            "&&": {
              left: 0,
              right: 0,
              width: "100%",
              maxWidth: "100%",
              margin: 0,
              bottom: 0,
              borderRadius: "22px 22px 0 0",
            },
            maxHeight: "min(78dvh, 620px)",
            height: "auto",
            bgcolor: "#F2F3F5",
            boxShadow: "0 -12px 40px rgba(15,15,18,0.22)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(78dvh, 620px)",
            pb: "calc(14px + env(safe-area-inset-bottom))",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1.15, pb: 0.25 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: "rgba(15,15,18,0.18)" }} />
          </Box>

          <Box
            sx={{
              px: 2.25,
              pb: 1.15,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.4, color: NAV.ink, lineHeight: 1.1 }}>
              {t("Menu")}
            </Typography>
            <ButtonBase
              onClick={() => setMenuOpen(false)}
              aria-label={t("Close")}
              sx={{
                width: 30,
                height: 30,
                borderRadius: "999px",
                bgcolor: "rgba(15,15,18,0.08)",
                color: NAV.muted,
              }}
            >
              <Icon sx={{ fontSize: 17 }}>close</Icon>
            </ButtonBase>
          </Box>

          <Box sx={{ overflowY: "auto", px: 1.5, WebkitOverflowScrolling: "touch" }}>
            <Stack spacing={1.75}>
              {menuSections.map((section) => (
                <Box key={section.key}>
                  <Typography
                    sx={{
                      px: 1.25,
                      mb: 0.6,
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: -0.1,
                      color: "#6E7278",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: "14px",
                      overflow: "hidden",
                    }}
                  >
                    {section.items.map((item, index) => (
                      <MenuRow
                        key={item.key}
                        item={item}
                        selected={item.to ? isActivePath(location.pathname, item.to) : false}
                        divider={index < section.items.length - 1}
                        onClick={() => handleMenuItem(item)}
                      />
                    ))}
                  </Box>
                </Box>
              ))}

              {showSignOut ? (
                <Box sx={{ bgcolor: "#fff", borderRadius: "14px", overflow: "hidden" }}>
                  <MenuRow
                    item={{ key: "sign-out", name: t("Sign Out") }}
                    danger
                    divider={false}
                    onClick={() => handleMenuItem({ key: "sign-out", to: "/m/sign-out" })}
                  />
                </Box>
              ) : null}
            </Stack>
          </Box>
        </Box>
      </SwipeableDrawer>

      <Box
        sx={{
          pt: ownsTopChrome ? 0 : "calc(env(safe-area-inset-top) + 8px)",
          pb: hideBottomNav
            ? 0
            : `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom) + ${MOBILE_BOTTOM_NAV_PAD}px + 28px)`,
        }}
      >
        {children}
      </Box>

      {!hideBottomNav ? (
        <Box
          component="nav"
          aria-label="Primary"
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1500,
            bgcolor: NAV.surface,
            backdropFilter: "blur(20px) saturate(1.2)",
            WebkitBackdropFilter: "blur(20px) saturate(1.2)",
            borderTop: `1px solid ${NAV.hairline}`,
            boxShadow: "0 -10px 28px rgba(15,15,18,0.06)",
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          <Box sx={{ height: 2, background: NAV.swoosh, opacity: 0.85 }} />
          <Box
            sx={{
              height: MOBILE_BOTTOM_NAV_HEIGHT,
              maxWidth: 720,
              mx: "auto",
              px: 1,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-around",
              gap: 0.25,
            }}
          >
            {bottomNavItems.map((item) => {
              const active = bottomNavValue === item.key;
              const badge = item.key === "cart" ? itemCount || 0 : 0;
              return (
                <NavTab
                  key={item.key}
                  item={item}
                  active={active}
                  badge={badge}
                  label={t(item.label)}
                  onClick={() => handleNav(item)}
                />
              );
            })}
          </Box>
        </Box>
      ) : null}
    </>
  );
}

MobileShell.propTypes = {
  children: PropTypes.node,
};
